/**
 * AnimationUtils.js
 * Reusable animation hooks and components for DataQuad dashboards.
 * 
 * Exports:
 *  - useCountUp(target, duration, decimals)  — animated number hook
 *  - useAnimateOnMount(delay)                — returns true after mount + delay
 *  - AnimatedNumber                          — renders a counting number
 *  - AnimatedProgressBar                     — width-animated bar
 *  - AnimatedCard                            — fade+slide-in wrapper with stagger
 *  - FadeInStagger                           — staggers children fade-in on mount
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Animates a number from 0 to `target` over `duration` ms.
 * Uses requestAnimationFrame for smooth 60fps interpolation.
 */
export const useCountUp = (target, duration = 800, decimals = 0, delay = 300) => {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);
  const timerRef = useRef(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (target === null || target === undefined || isNaN(target)) {
      setValue(0);
      return;
    }

    const to = Number(target);

    // Always start from 0 on mount with a visible delay
    setValue(0);
    hasStarted.current = false;

    timerRef.current = setTimeout(() => {
      hasStarted.current = true;
      const startTime = performance.now();
      // Use longer duration for small numbers so animation is perceptible
      const effectiveDuration = to <= 10 ? Math.max(duration, 600) : duration;

      const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / effectiveDuration, 1);
        // Ease-out cubic for natural deceleration
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = to * eased;

        setValue(Number(current.toFixed(decimals)));

        if (progress < 1) {
          frameRef.current = requestAnimationFrame(animate);
        } else {
          setValue(to);
        }
      };

      frameRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [target, duration, decimals, delay]);

  return value;
};

/**
 * Returns `true` once the component has been mounted for `delay` ms.
 * Useful for triggering CSS transitions on mount.
 */
export const useAnimateOnMount = (delay = 50) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return mounted;
};

/**
 * Intersection Observer hook — returns true when element is visible.
 * Animations only play when scrolled into view.
 */
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true); // fallback
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el); // only trigger once
        }
      },
      { threshold: 0.1, ...options }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
};

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * Renders a number that counts up from 0 to `value`.
 * 
 * <AnimatedNumber value={87.5} decimals={1} suffix="%" duration={1000} />
 */
export const AnimatedNumber = ({
  value,
  duration = 800,
  decimals = 0,
  delay = 300,
  prefix = '',
  suffix = '',
  style = {},
  className = '',
}) => {
  // Parse string-formatted numbers like "1,119" 
  const numericValue = typeof value === 'string' 
    ? parseFloat(value.replace(/[^0-9.\-]/g, '')) 
    : Number(value);
  
  const isValid = !isNaN(numericValue) && isFinite(numericValue);
  const displayed = useCountUp(isValid ? numericValue : 0, duration, decimals, delay);

  if (!isValid) {
    return <span className={className} style={style}>{prefix}{value}{suffix}</span>;
  }

  return (
    <span className={className} style={style}>
      {prefix}{displayed.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}{suffix}
    </span>
  );
};

/**
 * A progress bar that animates its width from 0 to `percentage`.
 * 
 * <AnimatedProgressBar percentage={75} color="#10B981" height={8} />
 */
export const AnimatedProgressBar = ({
  percentage = 0,
  color = '#3B82F6',
  backgroundColor = 'rgba(148, 163, 184, 0.15)',
  height = 8,
  borderRadius = 4,
  duration = 800,
  delay = 0,
  style = {},
}) => {
  const mounted = useAnimateOnMount(delay);

  return (
    <div
      style={{
        width: '100%',
        height,
        backgroundColor,
        borderRadius,
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      <div
        style={{
          height: '100%',
          width: mounted ? `${Math.min(percentage, 100)}%` : '0%',
          backgroundColor: color,
          borderRadius,
          transition: `width ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
          position: 'relative',
        }}
      />
    </div>
  );
};

/**
 * Wraps children in a fade + slide-up animation on mount.
 * Use `delay` for staggering multiple cards.
 * 
 * <AnimatedCard delay={100}>
 *   <MetricCard ... />
 * </AnimatedCard>
 */
export const AnimatedCard = ({
  children,
  delay = 0,
  duration = 500,
  translateY = 16,
  style = {},
  className = '',
}) => {
  const mounted = useAnimateOnMount(delay);

  return (
    <div
      className={className}
      style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : `translateY(${translateY}px)`,
        transition: `opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
        willChange: 'opacity, transform',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/**
 * Wraps each child in a staggered fade-in.
 * 
 * <FadeInStagger staggerMs={80}>
 *   <Card />
 *   <Card />
 *   <Card />
 * </FadeInStagger>
 */
export const FadeInStagger = ({
  children,
  staggerMs = 80,
  initialDelay = 0,
  duration = 450,
  translateY = 12,
  style = {},
  className = '',
}) => {
  return (
    <div className={className} style={style}>
      {React.Children.map(children, (child, index) => {
        if (!child) return null;
        return (
          <AnimatedCard
            delay={initialDelay + index * staggerMs}
            duration={duration}
            translateY={translateY}
          >
            {child}
          </AnimatedCard>
        );
      })}
    </div>
  );
};

/**
 * Animated donut/ring score indicator.
 * Draws an SVG circle that fills from 0 to `percentage`.
 * 
 * <AnimatedRing percentage={86} size={120} strokeWidth={10} color="#10B981" />
 */
export const AnimatedRing = ({
  percentage = 0,
  size = 120,
  strokeWidth = 10,
  color = '#10B981',
  trackColor = 'rgba(148, 163, 184, 0.15)',
  duration = 1000,
  delay = 200,
  children,
}) => {
  const mounted = useAnimateOnMount(delay);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (mounted ? (percentage / 100) * circumference : 0);

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Fill */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: `stroke-dashoffset ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
          }}
        />
      </svg>
      {/* Center content */}
      {children && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default {
  useCountUp,
  useAnimateOnMount,
  useInView,
  AnimatedNumber,
  AnimatedProgressBar,
  AnimatedCard,
  FadeInStagger,
  AnimatedRing,
};